import { Add, Delete, Edit } from '@mui/icons-material';
import { Box, Button, CircularProgress, Dialog, DialogContent, Grid, IconButton, Stack, TextField, Typography } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CommonDialog from '../../../common/CommonDialog';
import {
    addAdvisorApi,
    addTechnicianApi,
    deleteAdvisorApi,
    deleteTechnicianApi,
    getAdvisorListApi,
    getTechnicianListApi,
    updateAdvisorApi,
    updateTechnicianApi,
} from '../../../redux/Invoice/action';
import SuccessMessageModal from '../../Common/SuccessMessage';

/* ─── Table styles aligned with Shop Pricing → Fees.jsx (FEES_TABLE / header / rows) ─ */
const LIST_TABLE_OUTER = {
    width: '100%',
    p: '12px',
    border: '1px solid rgba(0, 0, 0, 0.10)',
    borderRadius: '8px',
    boxShadow: '0 2px 3px 0 rgba(0, 0, 0, 0.06)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    gap: '8px',
    backgroundColor: '#FFF',
};

const LIST_ROW_PADDING = {
    px: '16px',
    py: '8px',
    boxSizing: 'border-box',
    width: '100%',
};

const LIST_GRID_2COL = {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    alignItems: 'center',
};

const LIST_HEADER_ROW = {
    ...LIST_GRID_2COL,
    ...LIST_ROW_PADDING,
    backgroundColor: '#F8F8F8',
    fontSize: '14px',
    fontWeight: 600,
    color: '#1F1F1F',
};

/** Read-only row: name + action icons */
const LIST_DATA_ROW_VIEW = {
    ...LIST_GRID_2COL,
    ...LIST_ROW_PADDING,
};

/** Add / edit row: input + SAVE bottom-aligned, 12px between controls */
const LIST_DATA_ROW_EDIT = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: '12px',
    ...LIST_ROW_PADDING,
};

/** Name cell — Roboto 16 / 500 / 160% lh, letter-spacing 0.15px (design spec) */
const NAME_TEXT_STYLES = {
    fontFamily: 'Roboto',
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: 1.6,
    letterSpacing: '0.15px',
    fontFeatureSettings: "'liga' off",
    fontStyle: 'normal',
    color: 'rgba(0, 0, 0, 0.87)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
};

/** Inline name field — matches NAME_TEXT_STYLES when typing; placeholder 500 / #9E9E9E */
const INLINE_NAME_INPUT_SX = {
    flex: 1,
    minWidth: 0,
    marginTop: 0,
    marginBottom: 0,
    '& .MuiInputBase-root': {
        px: 0,
        py: 0,
        marginTop: 0,
        marginBottom: 0,
        alignItems: 'flex-end',
    },
    '& .MuiInputBase-input': {
        padding: 0,
        fontFamily: 'Roboto',
        fontSize: '16px',
        fontWeight: 500,
        lineHeight: 1.6,
        letterSpacing: '0.15px',
        fontFeatureSettings: "'liga' off",
        color: 'rgba(0, 0, 0, 0.87)',
    },
    '& .MuiInputBase-input::placeholder': {
        fontSize: '16px',
        fontWeight: 500,
        lineHeight: 1.6,
        letterSpacing: '0.15px',
        color: '#9E9E9E',
        opacity: 1,
    },
    '& .MuiInputBase-root:before': {
        borderBottom: '1px solid rgba(0,0,0,0.15)',
    },
    '& .MuiInputBase-root:hover:not(.Mui-disabled):before': {
        borderBottom: '1px solid rgba(0,0,0,0.30)',
    },
    '& .MuiInputBase-root:after': {
        borderBottom: '2px solid #006CDD',
    },
    '& .MuiFormHelperText-root': {
        marginLeft: 0,
        marginRight: 0,
        marginTop: '4px',
        fontSize: '11px',
    },
};

/** Row SAVE — padding 4×10 (hug), button/large typography */
const SAVE_BUTTON_SX = {
    flexShrink: 0,
    minWidth: 'unset',
    minHeight: 'unset',
    width: 'auto',
    height: 'auto',
    padding: '4px 10px',
    borderRadius: '6px',
    fontFamily: 'Roboto',
    fontSize: '15px',
    fontWeight: 500,
    lineHeight: '26px',
    letterSpacing: '0.46px',
    boxShadow: 'none',
    textTransform: 'uppercase',
    backgroundColor: '#E6F1FC',
    color: '#006CDD',
    '&:hover': {
        boxShadow: 'none',
        backgroundColor: '#D0E8FA',
    },
};

const ADD_BUTTON_SX = {
    width: '100px',
    height: '38px',
    padding: '6px 12px',
    marginTop: '13px',
    borderRadius: '6px',
    border: '1px solid #006CDD',
    background: '#FFF',
    color: '#006CDD',
    fontFamily: 'Roboto',
    fontSize: '15px',
    fontWeight: 500,
    lineHeight: '26px',
    textTransform: 'uppercase',
    '&:hover': {
        border: '1px solid #006CDD',
        background: '#FFF',
        boxShadow: 'none',
    },
};

/* ─── Helpers ───────────────────────────────────────────────────────────────── */
function normalizeName(value) {
    return (value || '').trim().toLowerCase();
}

function useInlineTableState() {
    const [mode, setMode] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [draftName, setDraftName] = useState('');
    const [rowError, setRowError] = useState('');

    const reset = useCallback(() => {
        setMode(null);
        setEditingId(null);
        setDraftName('');
        setRowError('');
    }, []);

    return { mode, editingId, draftName, rowError, setMode, setEditingId, setDraftName, setRowError, reset };
}

/* ─── Component ─────────────────────────────────────────────────────────────── */
const ShopTechniciansAndAdvisors = () => {
    const dispatch = useDispatch();

    const technicians = useSelector((state) => state?.invoice?.technicianList?.data) || [];
    const advisors = useSelector((state) => state?.invoice?.advisorList?.data) || [];

    const techState = useInlineTableState();
    const advisorState = useInlineTableState();

    const [isLoading, setIsLoading] = useState(false);
    const [openSuccessModal, setOpenSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    const [deleteLoader, setDeleteLoader] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [deleteContext, setDeleteContext] = useState(null);

    // Refs to track whether SAVE was clicked — one per kind
    const saveClickedRef = useRef({ technician: false, advisor: false });

    const fetchLists = useCallback(async () => {
        setIsLoading(true);
        try {
            await Promise.all([dispatch(getTechnicianListApi({})), dispatch(getAdvisorListApi({}))]);
        } finally {
            setIsLoading(false);
        }
    }, [dispatch]);

    useEffect(() => {
        fetchLists();
    }, [fetchLists]);

    const openAddRow = useCallback(
        (kind) => {
            const state = kind === 'technician' ? techState : advisorState;
            state.setMode('add');
            state.setEditingId(null);
            state.setDraftName('');
            state.setRowError('');
        },
        [advisorState, techState]
    );

    const openEditRow = useCallback(
        (kind, item) => {
            const state = kind === 'technician' ? techState : advisorState;
            state.setMode('edit');
            state.setEditingId(item?._id || null);
            state.setDraftName(item?.name || '');
            state.setRowError('');
        },
        [advisorState, techState]
    );

    const validateName = useCallback(
        (kind, nextName, editingId) => {
            const normalized = normalizeName(nextName);
            if (!normalized) return 'Please enter a name';
            const list = kind === 'technician' ? technicians : advisors;
            const exists = list.some((i) => normalizeName(i?.name) === normalized && i?._id !== editingId);
            if (exists) return `${kind === 'technician' ? 'Technician' : 'Advisor'} name already exists.`;
            return '';
        },
        [advisors, technicians]
    );

    const saveRow = useCallback(
        async (kind) => {
            const state = kind === 'technician' ? techState : advisorState;
            const { editingId, draftName } = state;
            const err = validateName(kind, draftName, editingId);
            state.setRowError(err);
            if (err) return;

            setIsLoading(true);
            try {
                const payload = { name: draftName.trim() };
                let result = false;
                if (kind === 'technician') {
                    if (state.mode === 'edit' && editingId) {
                        result = await updateTechnicianApi(editingId, payload);
                        if (result) setSuccessMessage('Technician updated successfully');
                    } else {
                        result = await addTechnicianApi(payload);
                        if (result) setSuccessMessage('Technician added successfully');
                    }
                } else {
                    if (state.mode === 'edit' && editingId) {
                        result = await updateAdvisorApi(editingId, payload);
                        if (result) setSuccessMessage('Advisor updated successfully');
                    } else {
                        result = await addAdvisorApi(payload);
                        if (result) setSuccessMessage('Advisor added successfully');
                    }
                }
                if (result) {
                    await dispatch(kind === 'technician' ? getTechnicianListApi({}) : getAdvisorListApi({}));
                    state.reset();
                    setOpenSuccessModal(true);
                }
            } finally {
                setIsLoading(false);
            }
        },
        [advisorState, dispatch, techState, validateName]
    );

    /**
     * Called when the TextField loses focus.
     * We defer by 150ms so that if the user clicked SAVE, the mousedown on
     * the button sets saveClickedRef BEFORE this handler checks it.
     */
    const handleInputBlur = useCallback(
        (kind) => {
            setTimeout(() => {
                if (saveClickedRef.current[kind]) {
                    // SAVE was clicked — let saveRow handle everything, just clear the flag
                    saveClickedRef.current[kind] = false;
                    return;
                }
                // Clicked outside — dismiss the row without saving
                const state = kind === 'technician' ? techState : advisorState;
                state.reset();
            }, 150);
        },
        [advisorState, techState]
    );

    const confirmDelete = useCallback((kind, id) => {
        if (!id) return;
        setDeleteContext({ kind, id });
        setIsDeleteModalOpen(true);
    }, []);

    const performDelete = useCallback(async () => {
        if (!deleteContext?.id || !deleteContext?.kind) return;
        setDeleteLoader(true);
        try {
            const { kind, id } = deleteContext;
            const result = kind === 'technician' ? await deleteTechnicianApi(id) : await deleteAdvisorApi(id);
            if (result) {
                await dispatch(kind === 'technician' ? getTechnicianListApi({}) : getAdvisorListApi({}));
                setSuccessMessage(`${kind === 'technician' ? 'Technician' : 'Advisor'} deleted successfully`);
                setOpenSuccessModal(true);
            }
        } finally {
            setDeleteLoader(false);
            setIsDeleteModalOpen(false);
            setDeleteContext(null);
        }
    }, [deleteContext, dispatch]);

    const renderTable = useCallback(
        ({ title, kind, items, state }) => (
            <Box sx={{ width: '100%' }}>
                <Box sx={LIST_TABLE_OUTER}>
                    <Box sx={LIST_HEADER_ROW}>
                        <Box component="span">{title}</Box>
                        <Box component="span" sx={{ justifySelf: 'end' }}>
                            Actions
                        </Box>
                    </Box>

                    {Array.isArray(items) &&
                        items.map((item) => {
                            const isEditing = state.mode === 'edit' && state.editingId === item?._id;
                            if (isEditing) {
                                return (
                                    <Box sx={LIST_DATA_ROW_EDIT} key={item?._id}>
                                        <TextField
                                            value={state.draftName}
                                            onChange={(e) => {
                                                state.setDraftName(e.target.value);
                                                if (state.rowError) state.setRowError('');
                                            }}
                                            onBlur={() => handleInputBlur(kind)}
                                            placeholder={`${kind === 'technician' ? 'Technician' : 'Advisor'} Name`}
                                            fullWidth
                                            error={!!state.rowError}
                                            helperText={state.rowError || undefined}
                                            variant="standard"
                                            sx={INLINE_NAME_INPUT_SX}
                                            autoFocus
                                        />
                                        <Button
                                            variant="contained"
                                            disableElevation
                                            onMouseDown={() => {
                                                // fires before the TextField's onBlur
                                                saveClickedRef.current[kind] = true;
                                            }}
                                            onClick={() => saveRow(kind)}
                                            disabled={isLoading}
                                            sx={SAVE_BUTTON_SX}
                                        >
                                            SAVE
                                        </Button>
                                    </Box>
                                );
                            }
                            return (
                                <Box sx={LIST_DATA_ROW_VIEW} key={item?._id}>
                                    <Typography variant="body1" sx={NAME_TEXT_STYLES}>
                                        {item?.name}
                                    </Typography>

                                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" sx={{ justifySelf: 'end' }}>
                                        <IconButton
                                            size="small"
                                            aria-label={`Edit ${kind}`}
                                            onClick={() => openEditRow(kind, item)}
                                            sx={{ p: '4px', width: 28, height: 28 }}
                                        >
                                            <Edit sx={{ fontSize: 20, color: '#646466' }} />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            aria-label={`Delete ${kind}`}
                                            onClick={() => confirmDelete(kind, item?._id)}
                                            sx={{ p: '4px', width: 28, height: 28 }}
                                        >
                                            <Delete sx={{ fontSize: 20, color: '#D32F2F' }} />
                                        </IconButton>
                                    </Stack>
                                </Box>
                            );
                        })}

                    {state.mode === 'add' && (
                        <Box sx={LIST_DATA_ROW_EDIT}>
                            <TextField
                                value={state.draftName}
                                onChange={(e) => {
                                    state.setDraftName(e.target.value);
                                    if (state.rowError) state.setRowError('');
                                }}
                                onBlur={() => handleInputBlur(kind)}
                                placeholder={`${kind === 'technician' ? 'Technician' : 'Advisor'} Name`}
                                fullWidth
                                error={!!state.rowError}
                                helperText={state.rowError || undefined}
                                variant="standard"
                                sx={INLINE_NAME_INPUT_SX}
                                autoFocus
                            />
                            <Button
                                variant="contained"
                                disableElevation
                                onMouseDown={() => {
                                    // fires before the TextField's onBlur
                                    saveClickedRef.current[kind] = true;
                                }}
                                onClick={() => saveRow(kind)}
                                disabled={isLoading}
                                sx={SAVE_BUTTON_SX}
                            >
                                SAVE
                            </Button>
                        </Box>
                    )}
                </Box>

                <Button variant="outlined" startIcon={<Add />} onClick={() => openAddRow(kind)} sx={ADD_BUTTON_SX} disableElevation>
                    ADD
                </Button>
            </Box>
        ),
        [confirmDelete, handleInputBlur, isLoading, openAddRow, openEditRow, saveRow]
    );

    return (
        <Box>
            <Grid
                container
                sx={{ boxShadow: 3, bgcolor: 'white' }}
                justifyContent="space-between"
                padding="24px"
                borderTop="none"
                borderRadius="10px"
                marginTop="32px"
            >
                {/* Section title */}
                <Grid item xs={12} sm={4} md={4} lg={3} xl={3}>
                    <Typography variant="h5" sx={{ fontWeight: 400, fontSize: '24px' }}>
                        Technicians &amp; Advisors
                    </Typography>
                </Grid>

                {/* Tables */}
                <Grid item xs={12} sm={8} md={8} xl={8}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            {renderTable({ title: 'Service Advisors', kind: 'advisor', items: advisors, state: advisorState })}
                        </Grid>
                        <Grid item xs={12} md={6}>
                            {renderTable({ title: 'Technicians', kind: 'technician', items: technicians, state: techState })}
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            {/* Delete confirmation */}
            <CommonDialog
                loader={deleteLoader}
                open={isDeleteModalOpen}
                onClose={() => {
                    if (!deleteLoader) {
                        setIsDeleteModalOpen(false);
                        setDeleteContext(null);
                    }
                }}
                onSave={performDelete}
                dialogTitle="Delete Confirmation"
                dialogSubtitle={`Are you sure you want to delete this ${deleteContext?.kind === 'advisor' ? 'Advisor' : 'Technician'}?`}
            />

            {/* Global loading spinner */}
            <Dialog fullScreen={false} open={isLoading} aria-labelledby="loading-dialog">
                <DialogContent>
                    <CircularProgress />
                </DialogContent>
            </Dialog>

            <SuccessMessageModal message={successMessage} open={openSuccessModal} setOpenSuccessModal={setOpenSuccessModal} />
        </Box>
    );
};

export default ShopTechniciansAndAdvisors;
